import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Download, IdCard, ShieldAlert, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

import { UltimaSincronizacaoCell } from '@/modulos/operacao/comum/UltimaSincronizacaoCell';
import {
  OPCOES_DE_ORIGEM,
  OPCOES_DE_SITUACAO_DA_PESSOA,
  situacaoComoSelo,
  type OrigemDoDado,
} from '@/modulos/operacao/comum/contrato';
import { formatarData } from '@/nucleo/formato/datas';
import { useUrlFilters } from '@/nucleo/ganchos/useUrlFilters';
import type { NaturezaDaPessoa } from '@/nucleo/pessoaNatureza';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { PageHeader } from '@/ui/PageHeader';
import { StatusBadge } from '@/ui/StatusBadge';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/base/tooltip';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';

import { OPCOES_DE_NATUREZA, naturezaDoTitular } from './contrato';
import { useBaixarTitularesCsv, useTitulares } from './dados';
import type { FiltroDeTitulares, Titular } from './contrato';

/**
 * `/operacao/titulares` — os proprietarios dos veiculos do TEG.
 *
 * TITULAR NAO E CREDENCIADO. O credenciado opera; o titular e dono do bem. Um credenciado
 * pode operar veiculos de terceiros, e um titular pode nao ter relacao nenhuma com o
 * programa alem da propriedade. Confundir os dois e o erro mais comum de quem chega nesta
 * tela pela primeira vez, e e por isso que a descricao dela diz o que ela e.
 *
 * LE DA API (`GET bff/v1/titulares`), sob o recorte do banco: a diretoria ve o titular
 * vigente de veiculo de credenciado da propria DRE (0012). O CPF chega mascarado, e a
 * lista nao traz CNPJ inteiro, telefone nem e-mail — contato fica no detalhe, que registra o acesso.
 *
 * E UMA TELA DE CONSULTA, E A FORMA DELA DIZ ISSO. Titular chega por integracao com o
 * SGTP; o TEG le e confere. Nao ha acao de criar nem de editar — nao por falta de
 * construir, mas porque criar titular a mao no TEG seria inventar proprietario de veiculo,
 * que e afirmacao sobre patrimonio de terceiro. O filtro de origem e a coluna de
 * sincronizacao sao o centro da tela justamente porque a pergunta aqui e "este dado esta
 * confiavel?".
 *
 * O CONFLITO DE INTEGRACAO GANHA SELO PROPRIO, ao lado da sincronizacao. Ele nao e o mesmo
 * que contingencia: contingencia e "a integracao estava fora e cadastramos a mao";
 * conflito e "a integracao respondeu, e a resposta briga com o que esta aqui". O segundo
 * exige decisao humana registrada, e nao se resolve esperando a integracao voltar.
 *
 * "ACOMPANHAR INTEGRACAO" APONTA PARA O CONECTOR GOV.BR, E ISSO E UMA DISCORDANCIA
 * DELIBERADA COM O PROTOTIPO, NAO UM ENGANO. Na tela de origem o botao levava a um painel
 * de sincronizacao de dados com o SGTP — que nao existe neste sistema. O unico painel de
 * integracao construido aqui e o do conector gov.br, e ele trata de OUTRO ASSUNTO: login
 * federado, nao reconciliacao de dado cadastral. O dono do repositorio decidiu, em
 * 2026-08-20, manter o botao mesmo assim, por fidelidade visual ao prototipo — sabendo que
 * o destino nao responde exatamente a pergunta que o rotulo sugere. Quando um painel de
 * status do SGTP existir, o destino deste botao deve migrar para la.
 */
export function TitularesPage() {
  const navigate = useNavigate();

  const padroes = useMemo(() => ({ search: '', status: '', natureza: '', origem: '' }), []);
  const { values, setFilter, clearAll, page, setPage, perPage, setPerPage } =
    useUrlFilters(padroes);

  const filtro = useMemo<FiltroDeTitulares>(
    () => ({
      pagina: page,
      tamanho: perPage,
      busca: values.search || undefined,
      situacao: values.status || undefined,
      natureza: (values.natureza || undefined) as NaturezaDaPessoa | undefined,
      origem: (values.origem || undefined) as OrigemDoDado | undefined,
    }),
    [page, perPage, values.search, values.status, values.natureza, values.origem],
  );

  const { data, isLoading, isError, error, refetch } = useTitulares(filtro);
  const linhas = useMemo(() => data?.itens ?? [], [data?.itens]);

  const temFiltro = Boolean(values.search || values.status || values.natureza || values.origem);

  const campos: FilterFieldConfig[] = [
    {
      key: 'search',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por nome, CPF ou CNPJ…',
      ariaLabel: 'Buscar titulares',
      flex: true,
    },
    {
      key: 'natureza',
      label: 'PF/PJ',
      type: 'select',
      options: [...OPCOES_DE_NATUREZA],
      width: 'w-full sm:w-[150px]',
    },
    {
      key: 'origem',
      label: 'Origem',
      type: 'select',
      options: [...OPCOES_DE_ORIGEM],
      width: 'w-full sm:w-[160px]',
    },
    {
      key: 'status',
      label: 'Situação',
      type: 'select',
      options: [...OPCOES_DE_SITUACAO_DA_PESSOA],
      width: 'w-full sm:w-[150px]',
    },
  ];

  const colunas = useMemo<TableViewColumn<Titular>[]>(
    () => [
      {
        id: 'nome',
        header: 'Nome',
        cell: (titular) => <span className="font-medium">{titular.nome}</span>,
        sortKey: 'nome',
        isCritical: true,
        canHide: false,
        linkedFilterKeys: ['search'],
      },
      {
        id: 'cpf_cnpj',
        header: 'CPF/CNPJ',
        // CPF e CNPJ chegam mascarados do banco; o inteiro so no detalhe, que registra o acesso.
        cell: (titular) => (
          <span className="font-mono text-sm">{titular.cpf ?? titular.cnpj ?? '—'}</span>
        ),
        linkedFilterKeys: ['search'],
      },
      {
        id: 'natureza',
        header: 'PF/PJ',
        // Sai do documento preenchido, não de um campo de tipo — ver `naturezaDoTitular`.
        cell: (titular) => <span className="text-sm">{naturezaDoTitular(titular) ?? '—'}</span>,
        linkedFilterKeys: ['natureza'],
      },
      {
        id: 'ultima_sincronizacao',
        header: 'Última sincronização',
        cell: (titular) => (
          <div className="flex items-start gap-1">
            <UltimaSincronizacaoCell
              origem={titular.origem_dado}
              sincronizadoEm={titular.sincronizado_em}
              emContingencia={titular.em_contingencia}
            />
            {titular.conflito_integracao && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="destructive" className="mt-0.5 cursor-help gap-1">
                      <ShieldAlert className="h-3 w-3" aria-hidden="true" />
                      Conflito
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    <p className="mb-1 font-semibold">O SGTP discorda deste cadastro</p>
                    <p className="text-muted-foreground">
                      A integração respondeu com dados diferentes dos que estão aqui. Precisa de
                      decisão registrada — não se resolve esperando. O detalhe está no titular.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        ),
        sortKey: 'sincronizado_em',
        linkedFilterKeys: ['origem'],
      },
      {
        id: 'status',
        header: 'Situação',
        cell: (titular) => (
          <StatusBadge status={situacaoComoSelo(titular.status)} label={titular.status} />
        ),
        sortKey: 'status',
        linkedFilterKeys: ['status'],
        isCritical: true,
        canHide: false,
        isStatusColumn: true,
      },
      {
        id: 'cadastro',
        header: 'Cadastro',
        cell: (titular) => (
          <span className="text-muted-foreground text-xs">{formatarData(titular.created_at)}</span>
        ),
        sortKey: 'created_at',
        isCadastroColumn: true,
      },
    ],
    [],
  );

  const tableView = useTableViewPreferences({
    contextKey: 'titulares:list',
    columns: colunas,
    filterValues: values,
  });

  const baixarCsv = useBaixarTitularesCsv();
  const [baixando, setBaixando] = useState(false);

  const exportar = async () => {
    setBaixando(true);
    try {
      const quantidade = await baixarCsv(filtro);
      toast('Planilha gerada', {
        description: `${quantidade} titular(es) no arquivo, com os filtros que você está usando.`,
      });
    } catch {
      toast.error('Não foi possível gerar o arquivo', {
        description: 'Tente novamente em instantes.',
      });
    } finally {
      setBaixando(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Titulares"
        description="Proprietários dos veículos do TEG, pessoa física ou jurídica. Vêm da integração com o SGTP — esta tela consulta e confere."
      >
        <Button
          variant="outline"
          onClick={() => void exportar()}
          disabled={baixando || isLoading}
          aria-label="Exportar titulares em CSV"
        >
          <Download className="h-4 w-4 sm:mr-2" aria-hidden="true" />
          <span className="hidden sm:inline">{baixando ? 'Gerando…' : 'Exportar CSV'}</span>
        </Button>
        <Button variant="outline" onClick={() => navigate('/admin/integracoes')}>
          <ShieldCheck className="h-4 w-4 sm:mr-2" aria-hidden="true" />
          <span className="hidden sm:inline">Acompanhar integração</span>
        </Button>
      </PageHeader>

      <FilterBar
        fields={campos}
        values={values}
        onChange={setFilter}
        onClear={clearAll}
        defaultValues={padroes}
      />

      {isError ? (
        <ErrorState
          variant="load-failed"
          description={
            error?.aviso ??
            'Não foi possível buscar os titulares agora. Verifique sua conexão e tente novamente.'
          }
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : (
        <DataTable
          columns={tableView.orderedColumns as DataTableColumn<Titular>[]}
          personalizacao={tableView}
          data={linhas}
          onRowClick={(titular) => navigate(`/operacao/titulares/${titular.slug}`)}
          isLoading={isLoading}
          total={data?.total}
          page={page}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          emptyIcon={IdCard}
          emptyTitle="Nenhum titular encontrado"
          emptyDescription={
            temFiltro
              ? 'Nenhum titular atende a esses filtros. Tente afrouxar a busca.'
              : 'Os titulares sincronizados do SGTP, ou cadastrados em contingência, aparecem aqui.'
          }
          minWidth="700px"
          ariaLabel="Lista de titulares"
        />
      )}
    </div>
  );
}

export default TitularesPage;
